import * as I from 'core/api/coreApiClient';
import { PayloadTypeConstants } from 'core/api/coreApiClient';

// Type guards for generic types generated from Aorta API schema

export interface RowSelectionSettingsResponse {
  type: PayloadTypeConstants;
  payload: I.RowSelectionSettings;
  messages: I.Message[];
}

type ItemTypes = I.Item | I.BaseItem | I.ReferenceItem | I.CollectionItem | I.MultiUnitsItem;
type BreadcrumbItemTypes = I.BreadcrumbItem | I.Breadcrumb | I.HierarchyGroup;
export type ResponseTypes = I.SectionResponse
  | I.PageResponse | I.AppStateResponse | I.GenericResponse
  | I.EmptyResponse | I.AutoCompletionResultResponse | I.ConfirmResponse
  | I.RedirectResponse | I.CommandResponse | RowSelectionSettingsResponse;

export const isEditPage = (page?: I.Page) => (page?.type === I.PageTypeConstants.EditPage);
export const isCreatePage = (page?: I.Page) => (page?.type === I.PageTypeConstants.CreatePage);
export const isWizardPage = (page?: I.Page) => (page?.type === I.PageTypeConstants.WizardPage);
export const isCreateOrWizardPage = (page?: I.Page) => isCreatePage(page) || isWizardPage(page);
export const isExtendedHierarchyViewPage = (page?: I.Page) => (
  page?.type === I.PageTypeConstants.ExtendedHierarchyViewPage
);

export const isBaseItem = (item?: ItemTypes): item is I.BaseItem => (item?.type === I.ItemTypeConstants.Base);
export const isReferenceItem = (item?: ItemTypes): item is I.ReferenceItem => (
  item?.type === I.ItemTypeConstants.Reference
);
export const isCollectionItem = (item?: ItemTypes): item is I.CollectionItem => (
  item?.type === I.ItemTypeConstants.Collection
);
export const isMultiUnitValuesItem = (item?: ItemTypes): item is I.MultiUnitsItem => (
  item?.type === I.ItemTypeConstants.MultiUnitValues
);
export const isItemWithValue = (item?: ItemTypes): item is I.BaseItem | I.ReferenceItem => (
  (item !== undefined && (
    item.type === I.ItemTypeConstants.Base
    || item.type === I.ItemTypeConstants.Reference
    || item.type === I.ItemTypeConstants.MultiUnitValues
  ))
);

export const isEmptyResponse = (response?: ResponseTypes): response is I.EmptyResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Empty
);
export const isPageResponse = (response?: ResponseTypes): response is I.PageResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Page
);
export const isSectionResponse = (response?: ResponseTypes): response is I.SectionResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Section
);
export const isConfirmResponse = (response?: ResponseTypes): response is I.ConfirmResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Confirm
);
export const isAppStateResponse = (response?: ResponseTypes): response is I.AppStateResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.AppState
);
export const isRedirectResponse = (response?: ResponseTypes): response is I.RedirectResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Redirect
);
export const isCopyToClipboardResponse = (response?: ResponseTypes): response is I.CopyToClipboardResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.CopyToClipboard
);
export const isResponseWithType = (response?: ResponseTypes): response is I.GenericResponse => (
  response !== undefined && Object.keys(PayloadTypeConstants).includes(response.type)
);
export const isAutoCompletionResultResponse = (
  response?: ResponseTypes,
): response is I.AutoCompletionResultResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.AutoCompletionResult
);
export const isCommandResponse = (response?: ResponseTypes): response is I.CommandResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Command
);
export const isFileDownloadCommand = (data?: unknown): data is I.Command => (
  data !== undefined && (data as I.Command).commandType === I.CommandTypeConstants.FileDownload
);
export const isCommandsResponse = (response?: ResponseTypes): response is I.CommandResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Commands
);
export const isIncidentResponse = (response?: ResponseTypes): response is I.IncidentResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.Incident
);
export const isRowSelectionSettingsResponse = (
  response?: ResponseTypes,
): response is RowSelectionSettingsResponse => (
  response !== undefined && response.type === I.PayloadTypeConstants.RowSelectionSettings
);

export const isLightTable = (data: unknown): data is I.LightTable => (
  data !== undefined && (data as I.LightTable).rows !== undefined
);
export const isRowSelection = (data: unknown): data is I.RowSelection => (
  data !== undefined && (data as I.RowSelection).rowIds !== undefined && (data as I.RowSelection).mode !== undefined
);
export const isFileUpload = (data: unknown): data is I.FileUpload => (
  data !== undefined && (data as I.FileUpload).fileMetaData !== undefined
);

interface PersistedRow extends I.Row { id: string }
export const isPersistedRow = (row: I.Row): row is PersistedRow => (row.id !== undefined);
export const isPersistedRows = (rows: I.Row[]): rows is PersistedRow[] => (!rows.some((row) => !isPersistedRow(row)));

export const isBreadcrumb = (item?: BreadcrumbItemTypes): item is I.Breadcrumb => (
  item?.type === I.BreadcrumbItemTypeConstants.Breadcrumb
);
export const isHierarchyGroup = (item?: BreadcrumbItemTypes): item is I.HierarchyGroup => (
  item?.type === I.BreadcrumbItemTypeConstants.HierarchyGroup
);

export const isValueEmpty = (value?: I.Value): value is undefined => {
  if (value) {
    const { item } = value;

    if (item) {
      if (isBaseItem(item) || isReferenceItem(item)) {
        return !item.value || item.value === '';
      }

      if (isCollectionItem(item)) {
        return !(item.items && item.items.length > 0);
      }

      if (isMultiUnitValuesItem(item)) {
        return !item.unitId || (((item.values || []).length === 0) && (!item.value || item.value === ''));
      }
    }
  }

  return true;
};

export const isUnits = (items: I.Unit[] | I.UnitValue[]): items is I.Unit[] => (
  items.length === 0 || ((items[0] as I.Unit).id !== undefined && (items[0] as I.Unit).label !== undefined)
);
